import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { setToken, formatApiError } from "@/lib/api";
import Logo from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    if (pw !== confirm) { toast.error("Passwords don't match"); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/reset-password", { token, new_password: pw });
      setToken(data.token);
      await refresh();
      toast.success("Password updated — you're logged in! 🎮");
      navigate("/app", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Couldn't reset password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#05060a] text-white p-6">
      <div className="w-full max-w-sm animate-rise">
        <div className="mb-8 flex justify-center"><Logo size="lg" /></div>
        {!token ? (
          <div className="text-center" data-testid="reset-invalid">
            <h1 className="font-display text-2xl font-bold mb-2">Invalid reset link</h1>
            <p className="text-white/50 text-sm mb-6">This link is missing or malformed. Please request a fresh one.</p>
            <Link to="/forgot-password" className="text-[#3b9eff] font-semibold" data-testid="request-new-link">Request a new link</Link>
          </div>
        ) : (
          <>
            <h1 className="font-display text-3xl font-bold mb-1">Set a new password</h1>
            <p className="text-white/50 mb-8 text-sm">Choose a strong password you'll remember.</p>
            <form onSubmit={submit} className="space-y-4">
              <div>
                <Label htmlFor="pw">New password</Label>
                <Input id="pw" type="password" data-testid="reset-password" value={pw} onChange={(e) => setPw(e.target.value)}
                  placeholder="••••••••" required className="mt-1.5 bg-white/5 border-white/10" />
              </div>
              <div>
                <Label htmlFor="confirm">Confirm password</Label>
                <Input id="confirm" type="password" data-testid="reset-confirm" value={confirm} onChange={(e) => setConfirm(e.target.value)}
                  placeholder="••••••••" required className="mt-1.5 bg-white/5 border-white/10" />
              </div>
              <Button type="submit" disabled={loading} data-testid="reset-submit"
                className="w-full rounded-full py-6 bg-gradient-to-r from-[#1f6feb] to-[#3b9eff] hover:opacity-90 brand-glow">
                {loading ? "Updating…" : "Update password"}
              </Button>
            </form>
            <p className="mt-6 text-sm text-white/50 text-center">
              <Link to="/login" className="text-[#3b9eff] font-semibold" data-testid="back-to-login">Back to log in</Link>
            </p>
          </>
        )}
      </div>
    </div>
  );
}
